import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { OpeningHoursDayDto, OpeningHoursDto } from '@workspace/data-types';

export type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface DayMetadata {
  key: DayKey;
  label: string;
}

export const DAYS_METADATA: DayMetadata[] = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
];

@Component({
  selector: 'aura-opening-hours-form',
  imports: [MatIconModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatInputModule],
  templateUrl: './opening-hours-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuraOpeningHoursFormComponent {
  readonly hours = input<OpeningHoursDto | undefined>(undefined);
  readonly title = input<string>("Horaires d'accueil et permanence");
  readonly showTitle = input<boolean>(true);

  readonly hoursChange = output<OpeningHoursDto>();

  readonly daysMeta = DAYS_METADATA;

  getDaySchedule(dayKey: DayKey): OpeningHoursDayDto {
    return (
      this.hours()?.[dayKey] || {
        isOpen: false,
        byAppointmentOnly: false,
      }
    );
  }

  isMorningOpen(sched: OpeningHoursDayDto): boolean {
    return Boolean(sched.morningOpen && sched.morningClose);
  }

  isAfternoonOpen(sched: OpeningHoursDayDto): boolean {
    return Boolean(sched.afternoonOpen && sched.afternoonClose);
  }

  toggleMorningOpen(dayKey: DayKey): void {
    const current = this.getDaySchedule(dayKey);
    const isOpen = this.isMorningOpen(current);
    if (isOpen) {
      this.updateDay(dayKey, {
        ...current,
        morningOpen: undefined,
        morningClose: undefined,
      });
    } else {
      this.updateDay(dayKey, {
        ...current,
        morningOpen: '09:00',
        morningClose: '12:00',
      });
    }
  }

  toggleAfternoonOpen(dayKey: DayKey): void {
    const current = this.getDaySchedule(dayKey);
    const isOpen = this.isAfternoonOpen(current);
    if (isOpen) {
      this.updateDay(dayKey, {
        ...current,
        afternoonOpen: undefined,
        afternoonClose: undefined,
      });
    } else {
      this.updateDay(dayKey, {
        ...current,
        afternoonOpen: '14:00',
        afternoonClose: '19:00',
      });
    }
  }

  toggleDayOpen(dayKey: DayKey): void {
    const current = this.getDaySchedule(dayKey);
    const willBeOpen = !current.isOpen;
    const hasAnySlot = Boolean(
      (current.morningOpen && current.morningClose) || (current.afternoonOpen && current.afternoonClose),
    );

    const updated: OpeningHoursDayDto = {
      ...current,
      isOpen: willBeOpen,
      morningOpen: willBeOpen && !hasAnySlot ? '09:00' : current.morningOpen,
      morningClose: willBeOpen && !hasAnySlot ? '12:00' : current.morningClose,
      afternoonOpen: willBeOpen && !hasAnySlot ? '14:00' : current.afternoonOpen,
      afternoonClose: willBeOpen && !hasAnySlot ? '19:00' : current.afternoonClose,
    };
    this.updateDay(dayKey, updated);
  }

  toggleDayByAppointment(dayKey: DayKey): void {
    const current = this.getDaySchedule(dayKey);
    this.updateDay(dayKey, {
      ...current,
      byAppointmentOnly: !current.byAppointmentOnly,
    });
  }

  onDayTimeChange(
    dayKey: DayKey,
    slot: 'morningOpen' | 'morningClose' | 'afternoonOpen' | 'afternoonClose',
    value: string,
  ): void {
    const current = this.getDaySchedule(dayKey);
    this.updateDay(dayKey, {
      ...current,
      [slot]: value || undefined,
    });
  }

  applyMondayToWeekdays(): void {
    const monday = this.getDaySchedule('monday');
    const current = this.hours() || {};
    this.hoursChange.emit({
      ...current,
      monday: { ...monday },
      tuesday: { ...monday },
      wednesday: { ...monday },
      thursday: { ...monday },
      friday: { ...monday },
    });
  }

  onNotesChange(notes: string): void {
    const current = this.hours() || {};
    this.hoursChange.emit({
      ...current,
      notes: notes.trim() || undefined,
    });
  }

  private updateDay(dayKey: DayKey, schedule: OpeningHoursDayDto): void {
    const current = this.hours() || {};
    this.hoursChange.emit({
      ...current,
      [dayKey]: schedule,
    });
  }
}
